// useProjectsData.ts — Sprint 12; rewired to the real backend in the
// Sprint 151.1 follow-up (ADR-147). Until then, add/update/delete mutated
// local Zustand state only and vanished on refresh (design-review #1). The
// hook now calls /api/projects through apiService and refreshes the store's
// `projects` from the server, so what the operator sees is what persisted.
//
// The write helpers deliberately DO NOT swallow errors — they let the
// apiService promise reject so the page can show a truthful success/error
// toast rather than an unconditional "saved".
import { useCallback } from 'react';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import React from 'react';
import { useCondoStore } from '../../store/condoStore';
import { apiService } from '../../services/api';
import { staticApi } from '../../utils/staticApi';
import type { Project } from './projectTypes';

/** The create payload: server owns id + timestamps. */
export type NewProject = Omit<Project, 'id' | 'createdAt' | 'updatedAt'>;

export interface UseProjectsData {
  projects: Project[];
  owners:   any[];

  /** Load this condo's projects into the store (call on mount / condo switch). */
  fetchProjects: (condoId: string) => Promise<void>;
  /** Persist a new project, then refresh from the server. Rejects on failure. */
  createProject: (project: NewProject) => Promise<void>;
  /** Persist an edit, then refresh from the server. Rejects on failure. */
  updateProject: (id: string, project: Partial<Project>) => Promise<void>;
  /** Confirm + delete + refresh, with its own success/error toast. */
  confirmDelete: (id: string, condoId: string) => void;
}

export function useProjectsData(): UseProjectsData {
  const { projects, owners, fetchProjects } = useCondoStore();

  const createProject = useCallback(async (project: NewProject) => {
    await apiService.createProject(project);
    await fetchProjects(project.condoId);
  }, [fetchProjects]);

  const updateProject = useCallback(async (id: string, project: Partial<Project>) => {
    await apiService.updateProject(id, project);
    if (project.condoId) await fetchProjects(project.condoId);
  }, [fetchProjects]);

  const confirmDelete = useCallback((id: string, condoId: string) => {
    staticApi.modal.confirm({
      title:   'Are you sure you want to delete this project?',
      icon:    React.createElement(ExclamationCircleOutlined),
      content: 'This action cannot be undone.',
      okType:  'danger',
      async onOk() {
        try {
          await apiService.deleteProject(id);
          await fetchProjects(condoId);
          staticApi.message.success('Project deleted successfully');
        } catch {
          staticApi.message.error('Failed to delete project');
        }
      },
    });
  }, [fetchProjects]);

  return {
    projects: projects as Project[],
    owners,
    fetchProjects,
    createProject,
    updateProject,
    confirmDelete,
  };
}
